import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { designer, navLinks } from '../data/mock';

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[70] px-8 md:px-[90px] transition-[opacity,background-color,backdrop-filter,padding-top,padding-bottom,box-shadow,border-color] duration-500 ${mounted ? 'opacity-100' : 'opacity-0'}`}
        style={{
          paddingTop: scrolled ? '1rem' : '1.75rem',
          paddingBottom: scrolled ? '1rem' : '1.75rem',
          background: scrolled ? 'rgba(27, 15, 13, 0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(18px) saturate(150%)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(18px) saturate(150%)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(232, 220, 207, 0.08)' : '1px solid transparent',
          boxShadow: scrolled ? '0 8px 24px -16px rgba(0, 0, 0, 0.7)' : 'none'
        }}
      >
        <div className="flex items-center justify-between">
          <Link to="/" className="font-label text-[0.78rem] tracking-[0.32em] text-[var(--cream)] hover:text-[var(--terracotta)] transition-colors duration-300">
            {designer.wordmark}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => {
              const active = location.pathname === link.to || (link.to === '/work' && location.pathname.startsWith('/work'));
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="relative font-label text-[0.72rem] text-[var(--cream)] transition-colors duration-300 hover:text-[var(--terracotta)] group"
                >
                  <span>{link.label}</span>
                  <span
                    className={`absolute -bottom-1 left-0 h-[1px] bg-[var(--terracotta)] transition-all duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] ${active ? 'w-full' : 'w-0 group-hover:w-full'}`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Mobile burger */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="md:hidden text-[var(--cream)] hover:text-[var(--terracotta)] transition-colors duration-300"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-[65] md:hidden transition-all duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'var(--bg-dark)' }}
      >
        <div className="h-full w-full flex flex-col items-center justify-center gap-8">
          {navLinks.map((link, i) => (
            <Link
              key={link.to}
              to={link.to}
              className="font-display text-5xl text-[var(--cream)] hover:text-[var(--terracotta)] transition-colors duration-300"
              style={{
                transform: open ? 'translateY(0)' : 'translateY(20px)',
                opacity: open ? 1 : 0,
                transition: `transform 600ms ease ${i * 100 + 200}ms, opacity 600ms ease ${i * 100 + 200}ms`
              }}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={`mailto:${designer.email}`}
            className="font-label text-xs text-[var(--cream)]/60 mt-12"
          >
            {designer.email}
          </a>
        </div>
      </div>
    </>
  );
};

export default Navigation;
